import { INTERACTIVE_LAYERS, LAYERS, UNIFIED_SOURCE } from '../utils/map-constants.js'
import { categorizeMandoUnico } from '../layers/geofence-renderer.js'

// Prioridad por categoria: cuadrante (mas especifico) > sector > region >
// geofence (mas general). Cuando varias geocercas se overlapan en un
// punto del click, queremos retornar la mas especifica para que el KPI
// muestre el area que el operador realmente clickeo, no el polígono
// macro que la contiene.
var GEOFENCE_CATEGORY_PRIORITY = { cuadrante: 4, sector: 3, region: 2, geofence: 1 }

// Heuristica de tamano: bbox area del fragmento de geometria que MapLibre
// retorna en queryRenderedFeatures (esta clipped a tiles del viewport,
// pero como todos los candidatos del overlap se consultaron contra el
// MISMO punto y MISMA capa, sus bboxes son comparables como proxy de
// "polígono mas chico" para desempatar dentro de la misma categoria.
function bboxAreaOfFeature(feature) {
  if (!feature || !feature.geometry) return Infinity
  var geom = feature.geometry
  var coords = geom.coordinates
  if (!coords) return Infinity
  var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  function visit(arr) {
    if (typeof arr[0] === 'number') {
      if (arr[0] < minX) minX = arr[0]
      if (arr[0] > maxX) maxX = arr[0]
      if (arr[1] < minY) minY = arr[1]
      if (arr[1] > maxY) maxY = arr[1]
    } else {
      for (var i = 0; i < arr.length; i++) visit(arr[i])
    }
  }
  visit(coords)
  if (!isFinite(minX)) return Infinity
  return (maxX - minX) * (maxY - minY)
}

// Selector definitivo de la geocerca clickeada. Ordena candidatos por
// (categoria DESC, area ASC) y retorna el primero — el mas especifico
// y, ante empate de categoria, el de bbox menor.
function pickMostSpecificGeofence(features) {
  if (!features || features.length === 0) return null
  if (features.length === 1) return features[0]
  var sorted = features.slice().sort(function(a, b) {
    var ca = categorizeMandoUnico(a.properties && a.properties.mandounico)
    var cb = categorizeMandoUnico(b.properties && b.properties.mandounico)
    var pa = GEOFENCE_CATEGORY_PRIORITY[ca] || 0
    var pb = GEOFENCE_CATEGORY_PRIORITY[cb] || 0
    if (pa !== pb) return pb - pa
    return bboxAreaOfFeature(a) - bboxAreaOfFeature(b)
  })
  return sorted[0]
}

// Tooltip en hover que muestra title + subtitle del monitor bajo el cursor.
// Complementa al label del symbol layer: cuando el label esta oculto por
// colision con otros labels, el hover lo trae a demanda. Un solo div
// reutilizable en screen coords del cursor. No usamos maplibregl.Popup
// para evitar el styling default (arrow, close btn) y mantener el look.
function createHoverTooltip(map) {
  var el = document.createElement('div')
  el.className = 'map-hover-tooltip'
  el.style.cssText = [
    'position: absolute',
    'pointer-events: none',
    'padding: 5px 9px',
    'background: rgba(10, 14, 26, 0.94)',
    'color: #fff',
    'font-family: "Open Sans", Roboto, sans-serif',
    'font-size: 11px',
    'line-height: 1.3',
    'border: 1px solid rgba(255, 255, 255, 0.16)',
    'border-radius: 4px',
    'max-width: 280px',
    'z-index: 5',
    'opacity: 0',
    'transition: opacity 120ms ease',
    'transform: translate(-50%, calc(-100% - 14px))',
    'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45)',
  ].join('; ')

  var titleEl = document.createElement('div')
  titleEl.style.cssText = [
    'font-weight: 700',
    'white-space: nowrap',
    'overflow: hidden',
    'text-overflow: ellipsis',
    'max-width: 264px',
  ].join('; ')

  var subtitleEl = document.createElement('div')
  subtitleEl.style.cssText = [
    'font-weight: 400',
    'opacity: 0.72',
    'font-size: 10px',
    'margin-top: 2px',
    'white-space: nowrap',
    'overflow: hidden',
    'text-overflow: ellipsis',
    'max-width: 264px',
  ].join('; ')

  el.appendChild(titleEl)
  el.appendChild(subtitleEl)
  map.getContainer().appendChild(el)
  return { root: el, titleEl: titleEl, subtitleEl: subtitleEl }
}

// Cuando hay varios markers empalmados bajo el cursor, MapLibre los devuelve
// en orden de stack (último renderizado primero). El primero NO siempre
// es el más cercano al click ni el de mayor prioridad operativa, lo que
// produce el bug "le doy click a un icono y selecciona otro".
//
// Algoritmo:
//   1. Si hay un único feature, retornarlo (caso común).
//   2. Sino, calcular distancia (en pixeles) del centro de cada feature
//      al point del click.
//   3. Ordenar por distancia ascendente.
//   4. Desempate dentro de TOLERANCE_PX: priorizar por status operativo
//      (alert > online > idle > offline), porque cuando varios están en
//      el mismo "cluster pixel" el operador típicamente apunta al de
//      mayor visibilidad (las alertas suelen estar arriba en draw order).
var STATUS_PRIORITY = { alert: 0, online: 1, idle: 2, offline: 3 }
var TOLERANCE_PX = 8

function pickClickedFeature(map, features, clickPoint) {
  if (features.length === 1) return features[0]
  var ranked = []
  for (var i = 0; i < features.length; i++) {
    var f = features[i]
    var coords = f.geometry && f.geometry.coordinates
    var dist = Infinity
    if (coords && coords.length >= 2) {
      var pt = map.project(coords)
      var dx = pt.x - clickPoint.x
      var dy = pt.y - clickPoint.y
      dist = Math.sqrt(dx * dx + dy * dy)
    }
    var prio = STATUS_PRIORITY[f.properties && f.properties.status]
    if (prio == null) prio = 99
    ranked.push({ f: f, dist: dist, prio: prio })
  }
  ranked.sort(function(a, b) {
    if (a.dist < TOLERANCE_PX && b.dist < TOLERANCE_PX) return a.prio - b.prio
    return a.dist - b.dist
  })
  return ranked[0].f
}

export function setupClickHandler(map, callbacks) {
  var tooltip = createHoverTooltip(map)

  function updateHoverTooltip(e) {
    var features = map.queryRenderedFeatures(e.point, { layers: INTERACTIVE_LAYERS })
    if (features.length === 0) {
      tooltip.root.style.opacity = '0'
      return
    }
    var props = features[0].properties
    var title = props.title
    if (!title) {
      tooltip.root.style.opacity = '0'
      return
    }
    tooltip.titleEl.textContent = title
    var subtitle = props.subtitle
    tooltip.subtitleEl.textContent = subtitle || ''
    tooltip.subtitleEl.style.display = subtitle ? 'block' : 'none'
    tooltip.root.style.left = e.point.x + 'px'
    tooltip.root.style.top = e.point.y + 'px'
    tooltip.root.style.opacity = '1'
  }

  function hideHoverTooltip() {
    tooltip.root.style.opacity = '0'
  }

  map.on('click', function(e) {
    // Si hay un consumer en modo "picker" (ej. Editar incidente pidiendo
    // las coords de la nueva ubicación), interceptamos el clic ANTES de
    // resolver features/clusters/geocercas. Sin esto, un clic sobre una
    // geocerca seleccionaría el KPI en vez de resolver el picker.
    if (typeof callbacks.isPickerActive === 'function' && callbacks.isPickerActive()) {
      callbacks.onPickerPick(e.lngLat)
      return
    }

    // Check the invisible cluster hit-target layer (unified-source, has cluster_id)
    var clusterFeatures = map.queryRenderedFeatures(e.point, {
      layers: [LAYERS.UNIFIED_CLUSTERS],
    })
    if (clusterFeatures.length > 0) {
      var f = clusterFeatures[0]
      if (f.properties.cluster_id != null) {
        callbacks.onClusterClick(f.properties.cluster_id, e.lngLat)
        return
      }
    }

    // Also check clicks on the cluster breakdown card images (cluster-img-source)
    if (map.getLayer(LAYERS.UNIFIED_CLUSTER_ICONS)) {
      var imgFeatures = map.queryRenderedFeatures(e.point, {
        layers: [LAYERS.UNIFIED_CLUSTER_ICONS],
      })
      if (imgFeatures.length > 0) {
        // Find the corresponding cluster in the unified source at this point
        var allAtPoint = map.queryRenderedFeatures(e.point, {
          layers: [LAYERS.UNIFIED_CLUSTERS],
        })
        if (allAtPoint.length > 0 && allAtPoint[0].properties.cluster_id != null) {
          callbacks.onClusterClick(allAtPoint[0].properties.cluster_id, e.lngLat)
          return
        }
        // If no cluster_id found, treat as a generic map click (edge case)
      }
    }

    var features = map.queryRenderedFeatures(e.point, { layers: INTERACTIVE_LAYERS })
    if (features.length > 0) {
      var feat = pickClickedFeature(map, features, e.point)
      var isCtrl = e.originalEvent.ctrlKey || e.originalEvent.metaKey
      callbacks.onFeatureClick(feat.properties.monitorId, feat.properties.entityType, feat.properties, e.lngLat, isCtrl)
    } else {
      // Click en geofence fill → KPI por geocerca. queryRenderedFeatures
      // retorna TODAS las geocercas que cubren el punto (cuadrante dentro
      // de sector dentro de region, etc.). El selector elige la mas
      // especifica para que el operador siempre obtenga el KPI del area
      // mas pequena que clickeo, no del polígono macro que la contiene.
      var geoFeatures = map.queryRenderedFeatures(e.point, { layers: [LAYERS.GEOFENCE_FILL] })
      if (geoFeatures.length > 0 && callbacks.onGeofenceClick) {
        var picked = pickMostSpecificGeofence(geoFeatures)
        if (picked) {
          callbacks.onGeofenceClick(picked.properties)
          return
        }
      }
      // onMapClick recibe el lngLat para que el consumidor pueda usarlo
      // en modos como el "location picker" (Editar incidente → seleccionar
      // ubicación en mapa). Si no necesita las coords, simplemente las
      // ignora — el callback es retro-compatible.
      callbacks.onMapClick(e.lngLat)
    }
  })

  map.on('contextmenu', function(e) {
    e.preventDefault()
    // Pasamos point (canvas-relative) y originalEvent (viewport-relative)
    // además del lngLat. El consumidor decide cuál usar según necesite
    // posicionar un overlay fixed (originalEvent.clientX/Y) vs absolute
    // dentro del canvas (point.x/y).
    callbacks.onContextMenu({
      lngLat: e.lngLat,
      point: e.point,
      originalEvent: e.originalEvent,
    })
  })

  // Cursor + hover tooltip para layers interactivos. mousemove mantiene el
  // tooltip anclado al cursor mientras esta dentro del layer; mouseleave
  // lo oculta. Uso mousemove (no mouseenter) para que el tooltip siga al
  // cursor si el usuario mueve el mouse dentro de un marker grande.
  map.on('mouseenter', LAYERS.UNIFIED_STATIC_ICONS, function() { map.getCanvas().style.cursor = 'pointer' })
  map.on('mousemove', LAYERS.UNIFIED_STATIC_ICONS, updateHoverTooltip)
  map.on('mouseleave', LAYERS.UNIFIED_STATIC_ICONS, function() {
    map.getCanvas().style.cursor = ''
    hideHoverTooltip()
  })
  map.on('mouseenter', LAYERS.UNIFIED_DYNAMIC_ICONS, function() { map.getCanvas().style.cursor = 'pointer' })
  map.on('mousemove', LAYERS.UNIFIED_DYNAMIC_ICONS, updateHoverTooltip)
  map.on('mouseleave', LAYERS.UNIFIED_DYNAMIC_ICONS, function() {
    map.getCanvas().style.cursor = ''
    hideHoverTooltip()
  })
  map.on('mouseenter', LAYERS.UNIFIED_CLUSTER_ICONS, function() { map.getCanvas().style.cursor = 'pointer' })
  map.on('mouseleave', LAYERS.UNIFIED_CLUSTER_ICONS, function() { map.getCanvas().style.cursor = '' })
}
